package com.wintermar.inspection.feature.presentation.inspection.answer

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.wintermar.inspection.core.domain.FlowResult
import com.wintermar.inspection.core.domain.Result
import com.wintermar.inspection.core.ui.component.datetime.util.DateTimeUtil
import com.wintermar.inspection.core.ui.component.image.util.FileChunkModel
import com.wintermar.inspection.core.ui.component.image.util.FileUploadStatus
import com.wintermar.inspection.core.ui.component.snackbar.util.SnackBarController
import com.wintermar.inspection.core.ui.component.snackbar.util.SnackBarPosition
import com.wintermar.inspection.core.ui.component.snackbar.util.SnackBarVariant
import com.wintermar.inspection.core.ui.util.SelectionModel
import com.wintermar.inspection.feature.domain.model.answer.AnswerModel
import com.wintermar.inspection.feature.domain.service.ChunkUploadCallback
import com.wintermar.inspection.feature.domain.service.ChunkUploadService
import com.wintermar.inspection.feature.domain.service.PendingUploadService
import com.wintermar.inspection.feature.domain.service.UploadTask
import com.wintermar.inspection.feature.domain.usecase.answer.SubmitAnswerUseCase
import com.wintermar.inspection.feature.domain.usecase.answer.UpsertAnswerUseCase
import com.wintermar.inspection.feature.domain.usecase.auth.GetUserUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.GetInspectionDetailUseCase
import com.wintermar.inspection.feature.domain.usecase.reference.GetListTemplateOptionUseCase
import com.wintermar.inspection.feature.domain.usecase.reference.GetListVesselUseCase
import com.wintermar.inspection.graphql.type.InspectionAnswerType
import com.wintermar.inspection.util.Constant
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.launch

abstract class AnswerViewModel(
    private val inspectionId: String,
    private val getInspectionDetailUseCase: GetInspectionDetailUseCase,
    private val getListTemplateOptionUseCase: GetListTemplateOptionUseCase,
    private val getListVesselUseCase: GetListVesselUseCase,
    private val getUserUseCase: GetUserUseCase,
    private val upsertAnswerUseCase: UpsertAnswerUseCase,
    private val chunkUploadService: ChunkUploadService,
    private val submitAnswerUseCase: SubmitAnswerUseCase,
    val pendingUploadService: PendingUploadService,
) : ViewModel() {
    abstract val state: AnswerState

    protected fun initAnswerData(skipDetail: Boolean = false) = viewModelScope.launch {
        getUser().join()
        getListAnswerOption()
        getListVessels()
        if (!skipDetail) {
            getInspectionDetail(inspectionId)
        }
    }

    private fun showError(message: String?) = viewModelScope.launch {
        SnackBarController.show(
            message = message ?: Constant.UNKNOWN_ERROR,
            variant = SnackBarVariant.ERROR
        )
    }

    private fun getUser() = viewModelScope.launch {
        when (val result = getUserUseCase()) {
            is Result.Success -> {
                result.data?.let {
                    state.user = it
                }
            }

            is Result.Error -> showError(result.error?.message)
        }
    }


    private fun getInspectionDetail(inspectionId: String) {
        getInspectionDetailUseCase(
            inspectionId = inspectionId,
            templateOptions = state.answerOptions
        ).onEach { result ->
            when (result) {
                is FlowResult.Loading -> {
                    state.isLoading = result.showLoading
                }

                is FlowResult.Success -> {
                    state.setDataTemplate(result.data?.template ?: state.template)
                    pendingUploadService.reattachOngoingUploads(buildChunkUploadCallback())
                }

                is FlowResult.Error -> showError(result.error?.message)

            }
        }.launchIn(viewModelScope)
    }

    private fun uploadFile(answerModel: AnswerModel) {
        chunkUploadService.enqueue(
            UploadTask(
                questionId = answerModel.questionId,
                files = answerModel.files.map { it.toFileModel() },
                callback = buildChunkUploadCallback()
            )
        )
    }

    private suspend fun getListVessels() {
        getListVesselUseCase().collect {
            when (it) {
                is FlowResult.Loading -> state.isLoading = it.showLoading
                is FlowResult.Success -> {
                    state.vessels = it.data?.map { data ->
                        SelectionModel(
                            id = data.id,
                            value = data.name
                        )
                    } ?: emptyList()
                }

                is FlowResult.Error -> showError(it.error?.message)
            }
        }
    }

    private suspend fun getListAnswerOption() {
        getListTemplateOptionUseCase().collect {
            when (it) {
                is FlowResult.Loading -> state.isLoading = it.showLoading
                is FlowResult.Success -> state.answerOptions = it.data ?: emptyList()
                is FlowResult.Error -> showError(it.error?.message)
            }
        }
    }

    private fun saveAnswer(answer: AnswerModel) = viewModelScope.launch {
        when (val result = upsertAnswerUseCase(answer)) {
            is Result.Success -> {
                //no-op
            }

            is Result.Error -> showError(result.error?.message)
        }
    }

    private suspend fun submitAnswer(
        answerModel: AnswerModel,
        answerType: InspectionAnswerType,
        includeUploadFile: Boolean,
        isUpdateAnswer: Boolean
    ) {
        if (answerModel.questionId.isEmpty() || answerModel.inspectionId.isEmpty() || answerModel.id.isEmpty()) {
            return
        }

        submitAnswerUseCase(answerModel, answerType).collect {
            if (answerType == InspectionAnswerType.USER_ID) return@collect
            if (it is FlowResult.Error) {
                val isNetworkError = it.error?.message?.contains(Constant.ERROR_NETWORK) == true
                if (isNetworkError && state.isOfflineMode) return@collect

                state.isOfflineMode = isNetworkError
                val errorMessage =
                    if (isNetworkError) Constant.WORKING_OFFLINE_MESSAGE else it.error?.message
                SnackBarController.show(
                    message = errorMessage ?: Constant.UNKNOWN_ERROR,
                    variant = if (isNetworkError) SnackBarVariant.WARNING else SnackBarVariant.ERROR,
                    position = SnackBarPosition.BOTTOM
                )
            }

            if (it is FlowResult.Success) {
                state.isOfflineMode = false

                if (isUpdateAnswer) saveAnswer(
                    answerModel.copy(
                        isSubmitted = true,
                        lastUpdatedIsoUtc0 = it.data ?: DateTimeUtil.getCurrentDateIsoUtc0()
                    )
                )

                if (answerModel.files.isNotEmpty() && includeUploadFile) uploadFile(
                    answerModel
                )
            }
        }
    }

    protected fun reattachUploadCallbackResult() {
        pendingUploadService.reattachOngoingUploads(buildChunkUploadCallback())
    }

    protected fun processAnswer(
        answer: AnswerModel,
        answerType: InspectionAnswerType,
        includeUploadFile: Boolean
    ) = viewModelScope.launch {
        processAnswerPlatform(
            answer = answer,
            answerType = answerType,
            includeUploadFile = includeUploadFile,
            onSaveAnswer = { saveAnswer(it) },
            onSubmitAnswer = { answer, answerType, includeUploadFile, isUpdateAnswer ->
                submitAnswer(answer, answerType, includeUploadFile, isUpdateAnswer)
            }
        )
    }

    protected fun buildChunkUploadCallback() = object : ChunkUploadCallback {
        override fun onProgress(questionId: String, chunk: FileChunkModel) {
            viewModelScope.launch {
                state.updateUploadFile(
                    questionId = questionId,
                    fileId = chunk.fileId,
                    currentUpload = chunk,
                    uploadStatus = FileUploadStatus.UPLOADING
                )
            }
        }

        override fun onCompleted(questionId: String, fileId: String, chunk: FileChunkModel?) {
            viewModelScope.launch {
                state.updateUploadFile(
                    questionId = questionId,
                    fileId = fileId,
                    currentUpload = chunk,
                    uploadStatus = FileUploadStatus.COMPLETE
                )
            }
        }

        override fun onError(questionId: String, fileId: String, error: Throwable?) {
            showError(error?.message)
        }

        override fun onDoneGenerateChunk(questionId: String, fileIds: List<String>) {
            viewModelScope.launch {
                state.updateHasGeneratedChunk(
                    questionId = questionId,
                    fileIds = fileIds
                )
            }
        }
    }
}

expect suspend fun processAnswerPlatform(
    answer: AnswerModel,
    answerType: InspectionAnswerType,
    includeUploadFile: Boolean,
    onSaveAnswer: suspend (AnswerModel) -> Unit,
    onSubmitAnswer: suspend (AnswerModel, InspectionAnswerType, Boolean, Boolean) -> Unit
)
